import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbs, Scrollbar, Mousewheel } from 'swiper';
import Image from 'utils/image';
import { trackPDPGalleryClick } from 'utils/tracking';
import { withProductContext } from './product-context';
import Video from './video';
import { SmallPlay, SliderArrow } from '../utils/icon/video-icons';
import ProductBadge from './product-badge';

const ProductGallery = (props) => {
  const {
    actualProduct: { media, tags },
  } = props;
  const { isQuickview } = props;

  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [swiper, setSwiper] = useState(null);
  const [caption, setCaption] = useState('');
  const [mediaAlt, setMediaAlt] = useState([]);
  const [previousVideo, setPreviousVideo] = useState(null);
  const [playCurrentVideo, setPlayCurrentVideo] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasSwipedFromFirst, setHasSwipedFromFirst] = useState(false);
  const [preloadVideoId, setPreloadVideoId] = useState(null);

  useEffect(() => {
    if (media && media.length > 0) {
      // eslint-disable-next-line max-len
      const mediaAltArray = media.map((mediaEl) => mediaEl && mediaEl.image && mediaEl.image.alt);
      setMediaAlt(mediaAltArray);

      if (mediaAltArray[0]) {
        const splitCaption = mediaAltArray[0].split('|');
        setCaption(splitCaption.length > 1 ? splitCaption[1].trim() : '');
      } else {
        setCaption('');
      }
    }
  }, [media]);

  const checkAvailability = (arr, val) => arr.some((arrVal) => arrVal.mediaContentType.toLowerCase() === val);

  const disablePreviousVideo = () => {
    if (!checkAvailability(media, 'video') && !checkAvailability(media, 'external_video')) {
      return;
    }

    const previousSlide = swiper.slides[swiper.previousIndex];
    const previousSlideIsVideo = previousSlide?.querySelector('video') || null;
    const previousSlideIsIframe = previousSlide?.querySelector('iframe') || null;

    if (previousSlideIsVideo) {
      setPreviousVideo(previousSlide.querySelector('[data-video-component]').id);
    } else if (previousSlideIsIframe) {
      setPreviousVideo(previousSlide.querySelector('[data-video-component]').id);
    }
  };

  const playSelectedVideo = () => {
    const activeSlide = swiper.slides[swiper.activeIndex];
    const slideIsVideo = activeSlide.querySelector('video') || null;
    const slideIsIframe = activeSlide.querySelector('iframe') || null;

    if (slideIsVideo) {
      setPlayCurrentVideo(activeSlide.querySelector('[data-video-component]').id);
    } else if (slideIsIframe) {
      setPlayCurrentVideo(activeSlide.querySelector('[data-video-component]').id);
    }
  };

  const slideChangeEvents = () => {
    disablePreviousVideo();
    playSelectedVideo();
    const { activeIndex: idx } = swiper;
    const slideAlt = mediaAlt[idx] ? mediaAlt[idx].split('|')[1]?.trim() : '';
    setCaption(slideAlt);
    setActiveIndex(idx);

    // After moving off the first slide on mobile, preload the upcoming video if near
    // Only enable preloading on mobile devices (below lg breakpoint)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    if (isMobile && idx > 0 && !hasSwipedFromFirst) {
      setHasSwipedFromFirst(true);
      // Find next media that is video within the next 2 slides
      const lookaheadRange = [idx, idx + 1, idx + 2];
      const targetIdx = lookaheadRange.find((lookIdx) => {
        const m = media?.[lookIdx];
        if (!m) return false;
        const type = m.mediaContentType?.toLowerCase();
        return type === 'video' || type === 'external_video';
      });
      if (Number.isInteger(targetIdx)) {
        const m = media[targetIdx];
        const id = m.id?.replace('gid://shopify/Video/', '').replace('gid://shopify/ExternalVideo/', '');
        setPreloadVideoId(`video-${id}`);
      }
    }
  };

  return (
    <div className="pdp-gallery-slider">
      <div className={`w-full lg:flex lg:gap-5 ${!isQuickview && 'lg:h-[660px]'} lg:overflow-hidden`}>
        {/* THUMBS SLIDER - DESKTOP ONLY */}
        {!isQuickview && (
          <div className="hidden w-max-[93px] min-w-[96px] lg:block h-[72vh] xl:h-auto">
            <Swiper
              className="w-full h-full overflow-y-scroll scrollbar-hide"
              modules={[Thumbs, Mousewheel]}
              onSwiper={setThumbsSwiper}
              threshold={10}
              direction="vertical"
              spaceBetween={10}
              mousewheel={true} // Enable mousewheel control
              breakpoints={{
                0: {
                  slidesPerView: 4,
                },
                1024: {
                  slidesPerView: 4,
                },
                1280: {
                  slidesPerView: 6.4,
                },
              }}
              watchSlidesVisibility={true}
              watchSlidesProgress={true}
            >
              {media?.map((mediaEl) => (
                <SwiperSlide
                  key={mediaEl.id}
                  className="w-full h-full overflow-hidden lg:!w-[95px] lg:!h-[95px] xl:w-full xl:h-full"
                >
                  <button
                    className="w-full h-full"
                    type="button"
                    tabIndex={0}
                    aria-label="Product media"
                    onClick={() => trackPDPGalleryClick('thumbnail')}
                  >
                    {mediaEl.mediaContentType.toLowerCase() === 'image' ? (
                      <Image
                        {...mediaEl.image}
                        height={93}
                        width={93}
                        srcWidths={{ sm: 250 }}
                        loading="eager"
                        classes="h-full object-contain"
                      />
                    ) : (
                      <div className="relative">
                        <Image
                          {...mediaEl.previewImage}
                          height={93}
                          width={93}
                          classes="w-[93px] h-[93px] object-cover"
                        />
                        <div className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full left-1/2 top-1/2">
                          <SmallPlay />
                        </div>
                      </div>
                    )}
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* MAIN SLIDER */}
        <div
          className={`relative mx-auto lg:overflow-hidden ${
            isQuickview ? 'w-full lg:w-[317px] h-full lg:h-[374px]' : 'xl:w-full lg:w-full lg:h-full'
          }`}
        >
          <Swiper
            modules={[Thumbs, Scrollbar]}
            thumbs={{ swiper: thumbsSwiper, autoScrollOffset: 1 }}
            navigation={{
              nextEl: '[data-product-slide-next]',
              prevEl: '[data-product-slide-prev]',
            }}
            scrollbar={{
              el: '[data-product-slider-scroll-bar]',
              draggable: true,
            }}
            mousewheel={{
              forceToAxis: true,
            }}
            onSwiper={setSwiper}
            onSlideChange={slideChangeEvents}
            onTouchMove={() => {
              setDragging(true);
            }}
            onTouchEnd={() => {
              if (dragging) {
                trackPDPGalleryClick('swipe');
                setDragging(false);
              } else {
                trackPDPGalleryClick('product image');
              }
            }}
            className="lg:w-full lg:h-full"
            breakpoints={{
              0: {
                slidesPerView: 1.2,
                spaceBetween: 5,
              },
              1024: {
                slidesPerView: 1,
                spaceBetween: 5,
              },
            }}
          >
            {media?.map((mediaEl, index) => (
              <SwiperSlide
                key={mediaEl.id}
                className={`w-full h-auto lg:max-h-[calc(100vh-142px)] 
                xl:max-h-[calc(100vh-142px)] xl:h-full 
                overflow-hidden relative ${
                  mediaEl.mediaContentType.toLowerCase() !== 'image' && 'bg-black grid items-center'
                }`}
              >
                {index === 0 && <ProductBadge tags={tags} />}
                {mediaEl.mediaContentType.toLowerCase() === 'image' ? (
                  <Image
                    {...mediaEl.image}
                    srcWidths={{
                      sm: 700,
                      '2xl': 800,
                    }}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    height={700}
                    width={700}
                    classes="lg:max-h-[calc(100vh-142px)] xl:max-h-[calc(100vh-142px)] lg:object-top lg:object-contain xl:object-cover mx-auto w-full lg:w-auto xl:w-auto"
                  />
                ) : (
                  <Video
                    video={mediaEl}
                    autoPlay={false}
                    controls
                    previousVideo={previousVideo}
                    playCurrentVideo={playCurrentVideo}
                    isActive={activeIndex === index}
                    shouldPreload={preloadVideoId === `video-${mediaEl.id?.replace('gid://shopify/Video/', '').replace('gid://shopify/ExternalVideo/', '')}`}
                    classes="w-full h-full"
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          <button
            className="absolute right-0 z-10 hidden top-1/4 xl:top-1/2 -translate-y-1/5 disabled:opacity-75 lg:block"
            data-product-slide-next
            type="button"
            aria-label="Arrow Right"
            onClick={() => trackPDPGalleryClick('arrow')}
          >
            <SliderArrow />
          </button>
          <button
            className="absolute left-0 z-10 hidden rotate-180 top-1/4 xl:top-1/2 -translate-y-1/5 disabled:opacity-75 lg:block"
            data-product-slide-prev
            type="button"
            aria-label="Arrow Left"
            onClick={() => trackPDPGalleryClick('arrow')}
          >
            <SliderArrow />
          </button>
        </div>
      </div>

      {/* SCROLL BAR - MOBILE ONLY */}
      <div
        className='w-full h-[2.5px] pt-2 px-0 pb-1 -mt-2 mb-[10px] relative before:content-[""] before:absolute before:w-full before:h-[2.5px] before:left-0 before:top-1/5 before:bg-grey-4 lg:hidden'
        data-product-slider-scroll-bar
      ></div>

      {/* CAPTION - DESKTOP ONLY */}
      {caption && (
        <div className="hidden w-full text-body-3 mt-[10px] px-5 pb-[25px] lg:pb-0 lg:px-0 lg:pl-[113px] xl:pl-[118px] lg:mt-2 lg:block">
          <span>{caption}</span>
        </div>
      )}
    </div>
  );
};

export default withProductContext(ProductGallery);
